import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Spinner, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('polling'); // polling | success | error
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    if (!sessionId) { setStatus('error'); setMessage('No session ID found'); return; }

    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2500;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('error');
        setMessage('Payment verification timed out. Please check your email for confirmation.');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Payment successful! Your listing is now live.');
          await refreshUser();
          return;
        }
        if (data.status === 'expired') {
          setStatus('error');
          setMessage('Payment session expired. Please try again.');
          return;
        }
        attempts++;
        setTimeout(poll, pollInterval);
      } catch {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 bg-[#F9F9F6]" data-testid="payment-success-page">
      <div className="bg-white border-2 border-[#004E89] rounded-2xl p-10 shadow-[4px_4px_0px_#004E89] max-w-md w-full text-center">
        {status === 'polling' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Spinner size={32} className="text-[#004E89] animate-spin" />
            </div>
            <h1 className="text-2xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Processing Payment</h1>
            <p className="text-[#5C7A99]" data-testid="payment-status-message">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#4CB944] border-2 border-[#004E89] flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={36} weight="fill" className="text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Payment Complete!</h1>
            <p className="text-[#5C7A99] mb-6" data-testid="payment-status-message">{message}</p>
            <div className="flex flex-col gap-3">
              <Link to="/my-listings">
                <button className="btn-primary w-full py-3" data-testid="go-to-listings-btn">View My Listings</button>
              </Link>
              <Link to="/explore">
                <button className="btn-secondary w-full py-3" data-testid="go-to-explore-btn">Explore Sales</button>
              </Link>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 border-2 border-red-400 flex items-center justify-center mx-auto mb-6">
              <Warning size={36} weight="fill" className="text-red-500" />
            </div>
            <h1 className="text-2xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Payment Issue</h1>
            <p className="text-[#5C7A99] mb-6" data-testid="payment-status-message">{message}</p>
            <Link to="/pricing">
              <button className="btn-primary w-full py-3" data-testid="retry-payment-btn">Try Again</button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
